// Adaptive Parameter Tuning System
// Automatically adjusts analytics parameters based on market conditions

import type { OrderBookEntry, Trade } from '../types';

export interface MarketConditions {
  volatility: number; // 0-1 scale
  liquidity: number; // 0-1 scale
  activity: number; // 0-1 scale
  spread: number; // relative spread
  updateFrequency: number; // updates per minute
}

export interface AdaptiveParameters {
  // Order flow parameters
  tradeClusterWindow: number; // seconds for clustering trades
  significantTradeThreshold: number; // minimum size for "large" trade

  // Book analysis parameters
  depthLevels: number; // number of levels to analyze
  spreadChangeThreshold: number; // % change considered significant
  imbalanceThreshold: number; // % imbalance considered significant

  // Volatility parameters
  volatilityWindow: number; // periods for volatility calculation
  priceChangeThreshold: number; // % change considered significant

  // Confidence parameters
  minimumDataPoints: number; // minimum observations for high confidence
  stalenessThreshold: number; // seconds before data considered stale

  // Cache parameters
  cacheTimeout: number; // milliseconds for cache expiry
  maxHistorySize: number; // maximum historical data points
}

export class AdaptiveParameterManager {
  private baseParameters: AdaptiveParameters;
  private currentParameters: AdaptiveParameters;
  private marketHistory: MarketConditions[] = [];
  private lastCalibration: number = 0;
  private readonly CALIBRATION_INTERVAL = 300000; // 5 minutes

  constructor(initialParams?: Partial<AdaptiveParameters>) {
    this.baseParameters = {
      tradeClusterWindow: 5,
      significantTradeThreshold: 2000,
      depthLevels: 5,
      spreadChangeThreshold: 0.1,
      imbalanceThreshold: 0.15,
      volatilityWindow: 30,
      priceChangeThreshold: 0.01,
      minimumDataPoints: 10,
      stalenessThreshold: 30,
      cacheTimeout: 30000,
      maxHistorySize: 10000,
      ...initialParams
    };
    this.currentParameters = { ...this.baseParameters };
  }

  // Analyze current market conditions
  analyzeMarketConditions(
    orderBook: { bids: OrderBookEntry[]; asks: OrderBookEntry[] },
    recentTrades: Trade[],
    priceHistory: Array<{ time: string; price: number }>
  ): MarketConditions {
    const volatility = this.calculateVolatility(priceHistory);
    const liquidity = this.calculateLiquidity(orderBook);
    const activity = this.calculateActivity(recentTrades);
    const spread = this.calculateRelativeSpread(orderBook);
    const updateFrequency = this.estimateUpdateFrequency(recentTrades);

    return {
      volatility: Math.max(0, Math.min(1, volatility)),
      liquidity: Math.max(0, Math.min(1, liquidity)),
      activity: Math.max(0, Math.min(1, activity)),
      spread: Math.max(0, spread),
      updateFrequency: Math.max(0, updateFrequency)
    };
  }

  private calculateVolatility(priceHistory: Array<{ time: string; price: number }>): number {
    if (priceHistory.length < 10) return 0.5; // default medium volatility

    const returns = [];
    for (let i = 1; i < Math.min(30, priceHistory.length); i++) {
      const prev = priceHistory[i - 1].price;
      const curr = priceHistory[i].price;
      if (prev > 0) {
        returns.push(Math.log(curr / prev));
      }
    }

    if (returns.length < 2) return 0.5;

    const mean = returns.reduce((sum, r) => sum + r, 0) / returns.length;
    const variance = returns.reduce((sum, r) => sum + Math.pow(r - mean, 2), 0) / returns.length;
    const volatility = Math.sqrt(variance) * Math.sqrt(252 * 24 * 60); // Annualized

    // Scale to 0-1 range (assuming 0.5 = average volatility)
    return Math.min(1, volatility / 0.5);
  }

  private calculateLiquidity(orderBook: { bids: OrderBookEntry[]; asks: OrderBookEntry[] }): number {
    const totalDepth = [
      ...orderBook.bids.slice(0, 10),
      ...orderBook.asks.slice(0, 10)
    ].reduce((sum, level) => sum + level.size, 0);

    // Scale based on typical market size (adjust for your market)
    const normalizedDepth = totalDepth / 100000; // Assuming 100k is high liquidity
    return Math.min(1, normalizedDepth);
  }

  private calculateActivity(recentTrades: Trade[]): number {
    const cutoff = Date.now() - (5 * 60 * 1000); // Last 5 minutes
    const recentCount = recentTrades.filter(trade => {
      const tradeTime = this.parseTradeTime(trade.time);
      return tradeTime > cutoff;
    }).length;

    // Scale: 0 trades = 0, 10+ trades in 5 min = 1.0
    return Math.min(1, recentCount / 10);
  }

  private calculateRelativeSpread(orderBook: { bids: OrderBookEntry[]; asks: OrderBookEntry[] }): number {
    const bestBid = orderBook.bids[0];
    const bestAsk = orderBook.asks[0];

    if (!bestBid || !bestAsk) return 1; // No spread data, assume wide

    const spread = bestAsk.price - bestBid.price;
    const midPrice = (bestBid.price + bestAsk.price) / 2;

    return midPrice > 0 ? spread / midPrice : 1;
  }

  private estimateUpdateFrequency(recentTrades: Trade[]): number {
    if (recentTrades.length < 2) return 0;

    const intervals = [];
    for (let i = 1; i < Math.min(20, recentTrades.length); i++) {
      const curr = this.parseTradeTime(recentTrades[i].time);
      const prev = this.parseTradeTime(recentTrades[i - 1].time);
      intervals.push(curr - prev);
    }

    const avgInterval = intervals.reduce((sum, i) => sum + i, 0) / intervals.length;
    return avgInterval > 0 ? 60000 / avgInterval : 0; // updates per minute
  }

  private parseTradeTime(timeString: string): number {
    const today = new Date();
    const [hours, minutes, seconds] = timeString.split(':').map(Number);
    return new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes, seconds).getTime();
  }

  // Adapt parameters based on market conditions
  adaptParameters(conditions: MarketConditions): AdaptiveParameters {
    const adapted = { ...this.baseParameters };

    // High volatility adjustments
    if (conditions.volatility > 0.7) {
      adapted.tradeClusterWindow *= 0.7; // Tighter clustering in volatile markets
      adapted.priceChangeThreshold *= 0.8; // Lower threshold for significant moves
      adapted.volatilityWindow *= 0.8; // Shorter lookback period
      adapted.cacheTimeout *= 0.5; // Faster cache expiry
    } else if (conditions.volatility < 0.3) {
      adapted.tradeClusterWindow *= 1.5; // Looser clustering in calm markets
      adapted.priceChangeThreshold *= 1.3; // Higher threshold for significant moves
      adapted.volatilityWindow *= 1.2; // Longer lookback period
    }

    // Low liquidity adjustments
    if (conditions.liquidity < 0.3) {
      adapted.depthLevels = Math.max(3, Math.round(adapted.depthLevels * 0.6)); // Fewer levels
      adapted.minimumDataPoints *= 0.7; // Lower confidence requirements
      adapted.significantTradeThreshold *= 0.5; // Lower threshold for significant trades
    } else if (conditions.liquidity > 0.7) {
      adapted.depthLevels = Math.min(15, Math.round(adapted.depthLevels * 1.5)); // More levels
      adapted.minimumDataPoints *= 1.3; // Higher confidence requirements
    }

    // High activity adjustments
    if (conditions.activity > 0.7) {
      adapted.stalenessThreshold *= 0.6; // Data gets stale faster
      adapted.cacheTimeout *= 0.7; // Faster cache refresh
      adapted.maxHistorySize *= 1.5; // Keep more history
    } else if (conditions.activity < 0.3) {
      adapted.stalenessThreshold *= 2; // Data stays fresh longer
      adapted.cacheTimeout *= 1.5; // Slower cache refresh
      adapted.maxHistorySize *= 0.8; // Keep less history
    }

    // Wide spread adjustments
    if (conditions.spread > 0.01) { // > 1% spread
      adapted.spreadChangeThreshold *= 1.5; // Need larger changes to be significant
      adapted.imbalanceThreshold *= 0.8; // More sensitive to imbalances
    }

    return adapted;
  }

  // Update parameters based on current market conditions
  updateParameters(
    orderBook: { bids: OrderBookEntry[]; asks: OrderBookEntry[] },
    recentTrades: Trade[],
    priceHistory: Array<{ time: string; price: number }>
  ): boolean {
    const now = Date.now();

    // Only recalibrate periodically to avoid thrashing
    if (now - this.lastCalibration < this.CALIBRATION_INTERVAL) {
      return false;
    }

    const conditions = this.analyzeMarketConditions(orderBook, recentTrades, priceHistory);

    // Store market conditions history
    this.marketHistory.push(conditions);
    if (this.marketHistory.length > 100) {
      this.marketHistory.shift();
    }

    // Adapt parameters
    const newParameters = this.adaptParameters(conditions);

    // Check if parameters changed significantly
    const hasSignificantChange = this.hasSignificantParameterChange(
      this.currentParameters,
      newParameters
    );

    if (hasSignificantChange) {
      this.currentParameters = newParameters;
      this.lastCalibration = now;

      // console.log('📊 Parameters adapted:', {
      //   volatility: conditions.volatility.toFixed(2),
      //   liquidity: conditions.liquidity.toFixed(2),
      //   activity: conditions.activity.toFixed(2),
      //   newParams: {
      //     tradeClusterWindow: newParameters.tradeClusterWindow,
      //     depthLevels: newParameters.depthLevels,
      //     cacheTimeout: newParameters.cacheTimeout
      //   }
      // });

      return true;
    }

    return false;
  }

  private hasSignificantParameterChange(old: AdaptiveParameters, newParams: AdaptiveParameters): boolean {
    const threshold = 0.1; // 10% change threshold

    const changes = [
      Math.abs(old.tradeClusterWindow - newParams.tradeClusterWindow) / old.tradeClusterWindow,
      Math.abs(old.depthLevels - newParams.depthLevels) / old.depthLevels,
      Math.abs(old.cacheTimeout - newParams.cacheTimeout) / old.cacheTimeout,
      Math.abs(old.minimumDataPoints - newParams.minimumDataPoints) / old.minimumDataPoints
    ];

    return changes.some(change => change > threshold);
  }

  // Getters
  getCurrentParameters(): AdaptiveParameters {
    return { ...this.currentParameters };
  }

  getMarketConditions(): MarketConditions[] {
    return [...this.marketHistory];
  }

  getLastCalibration(): number {
    return this.lastCalibration;
  }

  // Reset to base parameters
  reset(): void {
    this.currentParameters = { ...this.baseParameters };
    this.marketHistory = [];
    this.lastCalibration = 0;
  }

  // Export configuration for analysis
  exportConfiguration(): {
    baseParameters: AdaptiveParameters;
    currentParameters: AdaptiveParameters;
    marketHistory: MarketConditions[];
    adaptationHistory: Array<{
      timestamp: number;
      conditions: MarketConditions;
      parameters: AdaptiveParameters;
    }>;
  } {
    return {
      baseParameters: this.baseParameters,
      currentParameters: this.currentParameters,
      marketHistory: this.marketHistory,
      adaptationHistory: [] // Could be implemented to track parameter changes over time
    };
  }
}

// Singleton instance for global use
export const adaptiveParams = new AdaptiveParameterManager();